import { query, getDocs, where, orderBy, Timestamp, collection } from 'firebase/firestore';
import { feedbackCollection, db } from '../firebase';
import { isLegitimateFeedback } from './feedbackFilters';

// Loads the 7 GHMC zones once per export job. See:
//   apps/dashboards/ixora-dashboard/src/hooks/useZones.js
// for the live React hook used inside the UI.
async function loadZoneNameMap() {
  const snap = await getDocs(
    query(collection(db, 'sites'), where('areaNumber', '>=', 1), orderBy('areaNumber')),
  );
  const map = new Map();
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.active !== false) map.set(data.areaNumber, data.name);
  });
  return map;
}

function areaNumberToName(zoneNameMap, areaNum) {
  return zoneNameMap.get(areaNum) ?? 'Unknown Area';
}

// Format date as 'DD/MM/YYYY HH:MM AM/PM'
function formatDateTime(dateObj) {
  return dateObj.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Fetch feedbacks for a specific date
export async function fetchFeedbacksByDate(selectedDate) {
  try {
    // Create start and end timestamps for the selected date
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Convert to Firestore timestamps
    const startTimestamp = Timestamp.fromDate(startOfDay);
    const endTimestamp = Timestamp.fromDate(endOfDay);
    
    console.log(`Fetching feedbacks for date range: ${startOfDay.toISOString()} to ${endOfDay.toISOString()}`);
    
    // Query feedbacks within the date range
    const q = query(
      feedbackCollection,
      where('feedback_collection_DateTime', '>=', startTimestamp),
      where('feedback_collection_DateTime', '<=', endTimestamp),
      orderBy('feedback_collection_DateTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    // Exported reports must never include silent_duplicate fraud-trap rows.
    let feedbacks = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(isLegitimateFeedback);

    if (feedbacks.length === 0) {
      const dateKey = [
        selectedDate.getFullYear(),
        String(selectedDate.getMonth() + 1).padStart(2, '0'),
        String(selectedDate.getDate()).padStart(2, '0')
      ].join('-');

      const legacyDateQuery = query(
        feedbackCollection,
        where('feedback_collection_Date', '==', dateKey)
      );

      const legacySnapshot = await getDocs(legacyDateQuery);
      feedbacks = legacySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(isLegitimateFeedback);
      feedbacks.sort((a, b) => {
        const left = a.feedback_collection_DateTime?.seconds || 0;
        const right = b.feedback_collection_DateTime?.seconds || 0;
        return right - left;
      });
    }
    
    console.log(`Found ${feedbacks.length} feedbacks for the selected date`);
    return feedbacks;
  } catch (error) {
    console.error('Error fetching feedbacks by date:', error);
    return [];
  }
}

// Convert data to CSV format. Pass the zone name map loaded via loadZoneNameMap()
// so the export shows real zone names instead of area numbers.
export function convertToCSV(data, zoneNameMap = new Map()) {
  if (data.length === 0) return '';
  
  // Define CSV headers
  const headers = [
    'Sr No',
    'Date & Time',
    'Ixora User ID',
    'Supervisor Name',
    'Area',
    'Feedback Stars',
    'Phone Number',
    'Shop Name',
    'Shop Address',
    'App Version',
    'OTP Used'
  ];
  
  // Convert data to CSV rows
  const csvRows = [];
  csvRows.push(headers.join(','));
  
  data.forEach((item, index) => {
    const row = [
      index + 1,
      item.feedback_collection_DateTime ? 
        `"${formatDateTime(new Date(item.feedback_collection_DateTime.seconds * 1000))}"` : '',
      item.feedback_collector_ixora_userID || '',
      `"${item.feedback_collector_name || ''}"`,
      `"${item.shop_area ? areaNumberToName(zoneNameMap, parseInt(item.shop_area)) : ''}"`,
      item.feedback_stars != null ? item.feedback_stars : '',
      item.phone_number ? String(item.phone_number) : '',
      `"${item.shop_name || ''}"`,
      `"${item.shop_address || ''}"`,
      item.appVersion || '',
      item.otpUsed || ''
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

// Download CSV file
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    // Add to DOM and trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    URL.revokeObjectURL(url);
  }
}

// Main function to download feedbacks for a specific date
export async function downloadFeedbacksByDate(selectedDate) {
  try {
    // Format date for filename
    const dateStr = selectedDate.toLocaleDateString('en-GB').replace(/\//g, '-');
    const filename = `feedback_report_${dateStr}.csv`;
    
    console.log(`Downloading feedbacks for ${dateStr}...`);
    
    // Fetch feedbacks for the selected date
    const feedbacks = await fetchFeedbacksByDate(selectedDate);
    
    if (feedbacks.length === 0) {
      alert('No feedbacks found for the selected date.');
      return false;
    }
    
    // Load zone name map from Firestore so area numbers render as real names.
    const zoneNameMap = await loadZoneNameMap();

    // Convert to CSV and download
    const csvContent = convertToCSV(feedbacks, zoneNameMap);
    downloadCSV(csvContent, filename);
    
    console.log(`Downloaded ${feedbacks.length} feedbacks to ${filename}`);
    return true;
  } catch (error) {
    console.error('Error downloading feedbacks:', error);
    alert('Error downloading feedbacks. Please try again.');
    return false;
  }
}


